import { Loader2, Play, RotateCcw, Square } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  CrashingOutExecutionHistory,
  type CrashingOutPendingReview,
  type StepTrace,
} from '@/components/agentflow/CrashingOutExecutionHistory';
import { useCrashingOutSource } from '@/components/agentflow/useCrashingOutSource';
import { CoverageCardRedesign } from '@/components/coverage/shared/CoverageCardRedesign';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  useCancelCrashingOutMutation,
  useLazyGetCrashingOutHistoryQuery,
  useLazyGetCrashingOutPendingReviewsQuery,
  useLazyGetCrashingOutRunStatusQuery,
  useStartCrashingOutMutation,
  type WorkflowExecutionSummary,
} from '@/services/api/agentflowService';
import { useGetCoverageQuery } from '@/services/api/coverageService';
import type { CoverageListItem } from '@/services/types/coverage-types';
import axiosInstance from '@/services/axiosConfig';

const ACTIVE_RUN_STATUSES = new Set(['initializing', 'running', 'awaiting_review']);

const statusBadgeClass = (status: string): string => {
  switch (status) {
    case 'working':
    case 'running':
    case 'initializing':
      return 'bg-amber-100 text-amber-700';
    case 'needs_review':
    case 'awaiting_review':
      return 'bg-red-100 text-red-800';
    case 'completed':
      return 'bg-emerald-100 text-emerald-700';
    case 'failed':
    case 'timed_out':
      return 'bg-red-100 text-red-700';
    case 'terminated':
      return 'bg-zinc-200 text-zinc-700';
    case 'not_running':
    default:
      return 'bg-zinc-100 text-zinc-600';
  }
};

const formatAbsoluteDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const formatDuration = (startedAt: string, closedAt: string | null | undefined): string => {
  if (!closedAt) return 'In progress';
  try {
    const ms = new Date(closedAt).getTime() - new Date(startedAt).getTime();
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
    return `${Math.round(ms / 60_000)}m`;
  } catch {
    return '—';
  }
};

const getDefaultFormData = (schema: Record<string, unknown> | null | undefined) => {
  const defaults: Record<string, unknown> = {};
  const properties = (schema?.properties || {}) as Record<
    string,
    { default?: unknown; type?: string }
  >;

  Object.entries(properties).forEach(([key, property]) => {
    if (property.default !== undefined) {
      defaults[key] = property.default;
      return;
    }
    if (property.type === 'boolean') {
      defaults[key] = false;
      return;
    }
    defaults[key] = '';
  });

  return defaults;
};

const formatRunStartTime = (run: WorkflowExecutionSummary): string => {
  const startedAt = run.started_at ?? run.start_time;
  if (!startedAt) return run.run_id;
  try {
    return formatDistanceToNow(new Date(startedAt), { addSuffix: true });
  } catch {
    return startedAt;
  }
};

const getPreferredExecution = (
  executions: WorkflowExecutionSummary[],
  preferredRunId?: string | null,
): WorkflowExecutionSummary | null => {
  if (preferredRunId) {
    const preferredRun = executions.find((execution) => execution.run_id === preferredRunId);
    if (preferredRun) {
      return preferredRun;
    }
  }

  const activeRun = executions.find((execution) => ACTIVE_RUN_STATUSES.has(execution.status));
  if (activeRun) {
    return activeRun;
  }

  return executions[0] ?? null;
};

const parseJsonIfPossible = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const collectApprovalIds = (value: unknown, approvalIds: Set<string>): void => {
  if (value == null) {
    return;
  }

  const parsed = parseJsonIfPossible(value);
  if (typeof parsed === 'string' || typeof parsed === 'number' || typeof parsed === 'boolean') {
    return;
  }

  if (Array.isArray(parsed)) {
    parsed.forEach((item) => collectApprovalIds(item, approvalIds));
    return;
  }

  if (typeof parsed !== 'object') {
    return;
  }

  const record = parsed as Record<string, unknown>;
  const approvalId = record.approval_id;
  if (typeof approvalId === 'string' && approvalId.length > 0) {
    approvalIds.add(approvalId);
  }

  Object.values(record).forEach((child) => collectApprovalIds(child, approvalIds));
};

const getApprovalIdsFromTraces = (traces: StepTrace[]): Set<string> => {
  const approvalIds = new Set<string>();

  traces.forEach((trace) => {
    collectApprovalIds(trace.input, approvalIds);
    collectApprovalIds(trace.output, approvalIds);
  });

  return approvalIds;
};

const getNewestPendingReviews = (
  reviews: CrashingOutPendingReview[],
): CrashingOutPendingReview[] => {
  return [...reviews].sort((a, b) => {
    const aMs = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bMs = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bMs - aMs;
  });
};

export interface CrashingOutRunDetailsPanelProps {
  matterId: string;
  coverageId: string;
  preferredRunId?: string | null;
  initialDisplayStatus?: string | null;
  initialMatterName?: string | null;
  initialCoverageName?: string | null;
  initialInitiatedByName?: string | null;
  enabled?: boolean;
  variant?: 'modal' | 'inline';
}

export const CrashingOutRunDetailsPanel = ({
  matterId,
  coverageId,
  preferredRunId,
  initialDisplayStatus,
  initialMatterName,
  initialCoverageName,
  initialInitiatedByName,
  enabled = true,
  variant = 'modal',
}: CrashingOutRunDetailsPanelProps) => {
  const { source, setSource } = useCrashingOutSource();
  const { data: coverage, isLoading: isLoadingCoverage } = useGetCoverageQuery(
    { matterId, coverageId },
    { skip: !enabled },
  );
  const [fetchHistory] = useLazyGetCrashingOutHistoryQuery();
  const [fetchRunStatus] = useLazyGetCrashingOutRunStatusQuery();
  const [fetchPendingReviews] = useLazyGetCrashingOutPendingReviewsQuery();
  const [startCrashingOut, { isLoading: isStartingRun }] = useStartCrashingOutMutation();
  const [cancelCrashingOut, { isLoading: isStoppingRun }] = useCancelCrashingOutMutation();

  const [history, setHistory] = useState<WorkflowExecutionSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [traces, setTraces] = useState<StepTrace[]>([]);
  const [runStatus, setRunStatus] = useState<string | undefined>();
  const [loadedRunId, setLoadedRunId] = useState<string | null>(null);
  const [runStartTime, setRunStartTime] = useState<string | null | undefined>();
  const [runCloseTime, setRunCloseTime] = useState<string | null | undefined>();
  const [pendingReviews, setPendingReviews] = useState<CrashingOutPendingReview[]>([]);
  const [reviewFormData, setReviewFormData] = useState<Record<string, Record<string, unknown>>>({});
  const [submittingReviewId, setSubmittingReviewId] = useState<string | null>(null);
  const [isLoadingOverview, setIsLoadingOverview] = useState(false);
  const [isLoadingRunDetail, setIsLoadingRunDetail] = useState(false);
  const [isRestartingRun, setIsRestartingRun] = useState(false);
  const [restartDialogOpen, setRestartDialogOpen] = useState(false);
  const [restartInstructions, setRestartInstructions] = useState('');
  const overviewRequestRef = useRef(0);
  const runDetailRequestRef = useRef(0);
  const selectedRunIdRef = useRef<string | null>(null);
  const selectionContextRef = useRef<string | null>(null);

  useEffect(() => {
    selectedRunIdRef.current = selectedRunId;
  }, [selectedRunId]);

  useEffect(() => {
    const nextContextKey = enabled ? `${matterId}:${coverageId}:${preferredRunId ?? ''}` : null;
    if (selectionContextRef.current === nextContextKey) {
      return;
    }

    selectionContextRef.current = nextContextKey;
    const nextSelectedRunId = preferredRunId ?? null;
    selectedRunIdRef.current = nextSelectedRunId;
    setSelectedRunId(nextSelectedRunId);
  }, [coverageId, enabled, matterId, preferredRunId]);

  const selectedExecution = useMemo(
    () => history.find((execution) => execution.run_id === selectedRunId) ?? null,
    [history, selectedRunId],
  );

  const selectedRunTraces = useMemo(() => {
    if (!selectedExecution || loadedRunId !== selectedExecution.run_id) {
      return [];
    }
    return traces;
  }, [loadedRunId, selectedExecution, traces]);

  const selectedExecutionStatus = useMemo(() => {
    if (!selectedExecution) {
      return undefined;
    }
    if (loadedRunId === selectedExecution.run_id && runStatus) {
      return runStatus;
    }
    return selectedExecution.status;
  }, [loadedRunId, runStatus, selectedExecution]);

  const visiblePendingReviews = useMemo(() => {
    if (!selectedExecution || selectedExecutionStatus !== 'awaiting_review') {
      return [];
    }

    const reviewMatchesSelectedRun = pendingReviews.filter(
      (review) => review.source_run_id && review.source_run_id === selectedExecution.run_id,
    );
    if (reviewMatchesSelectedRun.length > 0) {
      return getNewestPendingReviews(reviewMatchesSelectedRun).slice(0, 1);
    }

    if (source === 'postgres') {
      return [];
    }

    const approvalIdsInSelectedRun = getApprovalIdsFromTraces(selectedRunTraces);
    if (approvalIdsInSelectedRun.size === 0) {
      return [];
    }

    const reviewMatchesTrace = pendingReviews.filter((review) =>
      approvalIdsInSelectedRun.has(review.approval_id),
    );
    if (reviewMatchesTrace.length === 0) {
      return [];
    }

    return getNewestPendingReviews(reviewMatchesTrace).slice(0, 1);
  }, [pendingReviews, selectedExecution, selectedExecutionStatus, selectedRunTraces, source]);

  const effectiveStatus = useMemo(() => {
    const rawStatus = selectedExecutionStatus || initialDisplayStatus || 'not_running';
    if (visiblePendingReviews.length > 0 && ACTIVE_RUN_STATUSES.has(rawStatus)) {
      return 'needs_review';
    }
    return rawStatus;
  }, [initialDisplayStatus, selectedExecutionStatus, visiblePendingReviews.length]);

  const hasActiveRun = useMemo(() => {
    return (
      ACTIVE_RUN_STATUSES.has(selectedExecutionStatus ?? '') ||
      effectiveStatus === 'working' ||
      effectiveStatus === 'needs_review'
    );
  }, [effectiveStatus, selectedExecutionStatus]);

  const loadOverview = useCallback(async () => {
    overviewRequestRef.current += 1;
    const requestId = overviewRequestRef.current;
    setIsLoadingOverview(true);
    try {
      const [historyResult, reviewsResult] = await Promise.all([
        fetchHistory({ matterId, coverageId, source }).unwrap(),
        fetchPendingReviews({ matterId, coverageId, source }).unwrap(),
      ]);

      if (requestId !== overviewRequestRef.current) {
        return;
      }

      const executions = historyResult.executions || [];
      const nextSelectedExecution = getPreferredExecution(executions, selectedRunIdRef.current);

      setHistory(executions);
      setSelectedRunId(nextSelectedExecution?.run_id ?? null);
      setPendingReviews(reviewsResult.reviews || []);

      const nextFormData: Record<string, Record<string, unknown>> = {};
      (reviewsResult.reviews || []).forEach((review) => {
        nextFormData[review.approval_id] = getDefaultFormData(review.response_schema);
      });
      setReviewFormData(nextFormData);
    } catch {
      toast.error('Failed to load agent run history');
    } finally {
      if (requestId === overviewRequestRef.current) {
        setIsLoadingOverview(false);
      }
    }
  }, [coverageId, fetchHistory, fetchPendingReviews, matterId, source]);

  const loadRunDetail = useCallback(
    async (runId: string) => {
      runDetailRequestRef.current += 1;
      const requestId = runDetailRequestRef.current;
      setIsLoadingRunDetail(true);
      setLoadedRunId(null);
      setTraces([]);
      setRunStatus(undefined);
      setRunStartTime(undefined);
      setRunCloseTime(undefined);
      try {
        const statusResult = await fetchRunStatus({
          matterId,
          coverageId,
          runId,
          source,
        }).unwrap();

        if (requestId !== runDetailRequestRef.current) {
          return;
        }

        setLoadedRunId(runId);
        setTraces((statusResult.traces || []) as unknown as StepTrace[]);
        setRunStatus(statusResult.workflow_status);
        setRunStartTime(statusResult.start_time);
        setRunCloseTime(statusResult.close_time);
      } catch {
        if (requestId === runDetailRequestRef.current) {
          setLoadedRunId(runId);
          toast.error('Failed to load run details');
        }
      } finally {
        if (requestId === runDetailRequestRef.current) {
          setIsLoadingRunDetail(false);
        }
      }
    },
    [coverageId, fetchRunStatus, matterId, source],
  );

  useEffect(() => {
    if (!enabled) {
      overviewRequestRef.current += 1;
      runDetailRequestRef.current += 1;
      setHistory([]);
      setSelectedRunId(null);
      setTraces([]);
      setRunStatus(undefined);
      setLoadedRunId(null);
      setRunStartTime(undefined);
      setRunCloseTime(undefined);
      setPendingReviews([]);
      setReviewFormData({});
      setSubmittingReviewId(null);
      setIsLoadingOverview(false);
      setIsLoadingRunDetail(false);
      return;
    }

    loadOverview().catch(() => undefined);
  }, [enabled, loadOverview]);

  useEffect(() => {
    if (!enabled || !selectedRunId) {
      setTraces([]);
      setRunStatus(undefined);
      setLoadedRunId(null);
      setRunStartTime(undefined);
      setRunCloseTime(undefined);
      return;
    }

    loadRunDetail(selectedRunId).catch(() => undefined);
  }, [enabled, loadRunDetail, selectedRunId]);

  const handleReviewFieldChange = useCallback(
    (approvalId: string, field: string, value: unknown) => {
      setReviewFormData((prev) => ({
        ...prev,
        [approvalId]: { ...(prev[approvalId] || {}), [field]: value },
      }));
    },
    [],
  );

  const handleSubmitReview = useCallback(
    async (approvalId: string) => {
      try {
        setSubmittingReviewId(approvalId);
        await axiosInstance.post(`/api/agentflow/eval/${matterId}/${approvalId}/submit/`, {
          action: 'submit',
          modified_data: reviewFormData[approvalId] || {},
        });
        toast.success('Review submitted');
        await loadOverview();
        const nextRunId = selectedRunId ?? preferredRunId ?? null;
        if (nextRunId) {
          await loadRunDetail(nextRunId);
        }
      } catch {
        toast.error('Failed to submit review');
      } finally {
        setSubmittingReviewId(null);
      }
    },
    [loadOverview, loadRunDetail, matterId, preferredRunId, reviewFormData, selectedRunId],
  );

  const handleStartRun = useCallback(async () => {
    try {
      selectedRunIdRef.current = null;
      setSelectedRunId(null);
      await startCrashingOut({
        matterId,
        coverageId,
        request: {},
      }).unwrap();
      toast.success('Crashing Out started');
      await loadOverview();
    } catch {
      toast.error('Failed to start Crashing Out');
    }
  }, [coverageId, loadOverview, matterId, startCrashingOut]);

  const handleStopRun = useCallback(async () => {
    try {
      await cancelCrashingOut({
        matterId,
        coverageId,
        request: { reason: 'Stopped from task details' },
      }).unwrap();
      toast.success('Crashing Out stopped');
      await loadOverview();
    } catch {
      toast.error('Failed to stop Crashing Out');
    }
  }, [cancelCrashingOut, coverageId, loadOverview, matterId]);

  const handleRestartRun = useCallback(async (instructions?: string) => {
    try {
      setIsRestartingRun(true);
      selectedRunIdRef.current = null;
      setSelectedRunId(null);
      if (hasActiveRun) {
        await cancelCrashingOut({
          matterId,
          coverageId,
          request: { reason: 'Restarted from task details' },
        }).unwrap();
      }
      await startCrashingOut({
        matterId,
        coverageId,
        request: instructions?.trim() ? { instructions: instructions.trim() } : {},
      }).unwrap();
      toast.success('Crashing Out restarted');
      setRestartDialogOpen(false);
      setRestartInstructions('');
      await loadOverview();
    } catch {
      toast.error('Failed to restart Crashing Out');
    } finally {
      setIsRestartingRun(false);
    }
  }, [cancelCrashingOut, coverageId, hasActiveRun, loadOverview, matterId, startCrashingOut]);

  const matterDisplay = initialMatterName || 'Unknown matter';
  const coverageDisplay =
    initialCoverageName ||
    coverage?.carrier?.name ||
    coverage?.named_insured?.[0] ||
    'Unknown coverage';
  const startedAt =
    (loadedRunId === selectedExecution?.run_id ? runStartTime : undefined) ??
    selectedExecution?.started_at ??
    selectedExecution?.start_time ??
    null;
  const closedAt =
    (loadedRunId === selectedExecution?.run_id ? runCloseTime : undefined) ??
    selectedExecution?.close_time ??
    null;
  const showCoverageCard = variant !== 'inline';
  const showInlineRunActions = variant === 'inline';
  const isMutatingRun = isStartingRun || isStoppingRun || isRestartingRun;

  const coverageContent = isLoadingCoverage ? (
    <Skeleton className="h-40 w-full rounded-xl" />
  ) : coverage ? (
    <CoverageCardRedesign
      coverage={coverage as unknown as CoverageListItem}
      matterId={matterId}
      readOnly
      forceShowProgressStepper
    />
  ) : (
    <div className="rounded-lg border border-dashed border-zinc-200 px-4 py-6 text-sm text-zinc-500">
      Coverage details unavailable.
    </div>
  );

  const historyContent = isLoadingOverview || isLoadingRunDetail ? (
    <div className="space-y-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} className="h-12 w-full" />
      ))}
    </div>
  ) : selectedExecution && loadedRunId !== selectedExecution.run_id ? (
    <div className="space-y-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} className="h-12 w-full" />
      ))}
    </div>
  ) : selectedExecution ? (
    <CrashingOutExecutionHistory
      traces={selectedRunTraces}
      runStatus={selectedExecutionStatus}
      matterId={matterId}
      pendingReviews={visiblePendingReviews}
      reviewFormDataByApproval={reviewFormData}
      submittingReviewId={submittingReviewId}
      onReviewFieldChange={handleReviewFieldChange}
      onSubmitReview={handleSubmitReview}
      defaultExpandLast
      focusPendingReview={visiblePendingReviews.length > 0}
    />
  ) : (
    <div className="px-2 py-6 text-sm text-zinc-500">
      No Crashing Out runs found for this coverage.
    </div>
  );

  return (
    <div className={cn('flex min-h-0 flex-col bg-zinc-50', variant === 'modal' && 'h-full')}>
      <div className="shrink-0 border-b border-zinc-200 bg-white px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-zinc-900">Agent Run Details</div>
            <div className="mt-1 text-xs text-zinc-600">
              {matterDisplay} · {coverageDisplay}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Select value={source} onValueChange={(value) => setSource(value as typeof source)}>
              <SelectTrigger className="h-7 w-[120px] bg-white text-[11px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="temporal">Temporal</SelectItem>
                <SelectItem value="postgres">Postgres</SelectItem>
              </SelectContent>
            </Select>
            <Badge className={cn('text-[10px] whitespace-nowrap', statusBadgeClass(effectiveStatus))}>
              {effectiveStatus.replace('_', ' ')}
            </Badge>
            {showInlineRunActions && (
              <>
                {hasActiveRun ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="h-7 gap-1.5 px-2 text-[11px]"
                    onClick={handleStopRun}
                    disabled={isMutatingRun}
                  >
                    {isStoppingRun ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3.5 w-3.5" />}
                    {isStoppingRun ? 'Stopping...' : 'Stop'}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 gap-1.5 px-2 text-[11px]"
                    onClick={handleStartRun}
                    disabled={isMutatingRun}
                  >
                    {isStartingRun ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                    {isStartingRun ? 'Starting...' : 'Start'}
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1.5 px-2 text-[11px]"
                  onClick={() => setRestartDialogOpen(true)}
                  disabled={isMutatingRun}
                >
                  {isRestartingRun ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  {isRestartingRun ? 'Restarting...' : 'Restart'}
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-2">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">Started</div>
            <div className="mt-0.5 text-[11px] text-zinc-800">
              {formatAbsoluteDateTime(startedAt)}
            </div>
          </div>
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-2">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">Closed</div>
            <div className="mt-0.5 text-[11px] text-zinc-800">
              {formatAbsoluteDateTime(closedAt)}
            </div>
          </div>
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-2">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">Duration</div>
            <div className="mt-0.5 text-[11px] text-zinc-800">
              {startedAt ? formatDuration(startedAt, closedAt) : '—'}
            </div>
          </div>
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-2">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">Initiated By</div>
            <div className="mt-0.5 truncate text-[11px] text-zinc-800">
              {initialInitiatedByName || '—'}
            </div>
          </div>
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-2">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">Run ID</div>
            <div className="mt-0.5 font-mono text-[11px] text-zinc-800">
              {selectedExecution?.run_id ? `${selectedExecution.run_id.slice(0, 12)}...` : '—'}
            </div>
          </div>
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-2">
            <div className="text-[10px] uppercase tracking-wide text-zinc-500">Reviews</div>
            <div className="mt-0.5 text-[11px] text-zinc-800">{visiblePendingReviews.length}</div>
          </div>
        </div>
        {history.length > 1 ? (
          <div className="mt-3 max-w-sm">
            <Select value={selectedRunId ?? undefined} onValueChange={(value) => setSelectedRunId(value)}>
              <SelectTrigger className="h-8 bg-white text-xs">
                <SelectValue placeholder="Select execution" />
              </SelectTrigger>
              <SelectContent>
                {history.map((execution) => (
                  <SelectItem key={execution.run_id} value={execution.run_id}>
                    {execution.status.replace('_', ' ')} · {formatRunStartTime(execution)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>

      <div
        className={cn(
          'px-4 py-4 sm:px-5',
          variant === 'modal' && 'min-h-0 flex-1 overflow-y-auto',
        )}
      >
        <div className="space-y-4">
          {showCoverageCard && (
            <div className="rounded-lg border border-zinc-200 bg-white p-3 sm:p-4">
              {coverageContent}
            </div>
          )}
          <div className="rounded-lg border border-zinc-200 bg-white p-3 sm:p-4">{historyContent}</div>
        </div>
      </div>

      <Dialog
        open={restartDialogOpen}
        onOpenChange={(open) => {
          setRestartDialogOpen(open);
          if (!open && !isRestartingRun) {
            setRestartInstructions('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">
              {hasActiveRun ? 'Stop & Restart Crashing Out' : 'Restart Crashing Out'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-zinc-600">
              <span className="font-medium text-zinc-800">{matterDisplay}</span>
              {' — '}
              {coverageDisplay}
            </div>
            {hasActiveRun ? (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                This will stop the current workflow before starting a new one.
              </div>
            ) : null}
            <div className="space-y-1.5">
              <p className="text-sm font-medium text-zinc-700">Extra instructions (optional)</p>
              <Textarea
                placeholder="Add any extra instructions for this run..."
                value={restartInstructions}
                onChange={(event) => setRestartInstructions(event.target.value)}
                rows={4}
                className="text-sm"
                disabled={isRestartingRun}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRestartDialogOpen(false)}
                disabled={isRestartingRun}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  handleRestartRun(restartInstructions);
                }}
                disabled={isRestartingRun}
                className="gap-1.5"
              >
                {isRestartingRun ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="h-3.5 w-3.5" />
                )}
                {isRestartingRun
                  ? hasActiveRun
                    ? 'Stopping & Restarting...'
                    : 'Restarting...'
                  : hasActiveRun
                    ? 'Stop & Restart'
                    : 'Restart'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
